import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { apiErrorMessage } from '@/services/apiClient';
import type { PdType } from '@optiflow/types';
import { useRecordMeasurementMutation, type MeasurementInput } from '../api/measurements.api';

const BLANK = {
  rightPdMm: '',
  leftPdMm: '',
  totalPdMm: '',
  faceWidthMm: '',
  bridgeWidthMm: '',
  eyeWidthMm: '',
  faceLengthMm: '',
  notes: '',
};

/**
 * Recording a measurement taken by hand.
 *
 * Every field stays optional. A ruler across the bridge gives a total and no monoculars, and
 * demanding all three would turn a normal way of working into an error message. The total fills
 * itself in from the two monoculars as they are typed, and remains editable — an optician who
 * rounds it differently is not wrong.
 */
export function RecordMeasurementDialog({
  customerId,
  isOpen,
  onClose,
}: {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const record = useRecordMeasurementMutation(customerId);

  const [values, setValues] = useState(BLANK);
  const [pdType, setPdType] = useState<PdType>('DISTANCE');
  const [totalTouched, setTotalTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setValues(BLANK);
    setPdType('DISTANCE');
    setTotalTouched(false);
    setError(null);
  }, [isOpen]);

  const set = (field: keyof typeof BLANK, value: string) =>
    setValues((current) => ({ ...current, [field]: value }));

  /**
   * Adds the monoculars in tenths, never as decimals.
   *
   * `31.7 + 32.6` is `64.30000000000001` in binary, which would put a number on screen that the
   * server then refuses. Integer tenths give the value the optician expects.
   */
  const suggestedTotal = (() => {
    const tenths = (value: string) => {
      const match = /^(\d{1,3})(?:\.(\d))?$/.exec(value.trim());
      return match ? Number(match[1]) * 10 + Number(match[2] ?? 0) : null;
    };

    const right = tenths(values.rightPdMm);
    const left = tenths(values.leftPdMm);
    if (right === null || left === null) return null;

    const sum = right + left;
    return `${Math.floor(sum / 10)}.${sum % 10}`;
  })();

  const total = totalTouched || !suggestedTotal ? values.totalPdMm : suggestedTotal;

  const submit = () => {
    setError(null);

    const payload: MeasurementInput = Object.fromEntries(
      Object.entries({ ...values, totalPdMm: total })
        .map(([field, value]) => [field, value.trim()])
        .filter(([, value]) => value !== ''),
    );
    // The type travels with a PD and never without one.
    if (payload.rightPdMm || payload.leftPdMm || payload.totalPdMm) payload.pdType = pdType;

    record.mutate(payload, {
      onSuccess: onClose,
      onError: (caught) => setError(apiErrorMessage(caught) ?? t('measurements.errors.record')),
    });
  };

  const hasSomething = Object.entries({ ...values, totalPdMm: total }).some(
    ([field, value]) => field !== 'notes' && value.trim() !== '',
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('measurements.add')}
      description={t('measurements.addHint')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} isLoading={record.isPending} disabled={!hasSomething}>
            {t('measurements.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <MillimetreField
            label={t('measurements.fields.rightPd')}
            value={values.rightPdMm}
            onChange={(value) => set('rightPdMm', value)}
          />
          <MillimetreField
            label={t('measurements.fields.leftPd')}
            value={values.leftPdMm}
            onChange={(value) => set('leftPdMm', value)}
          />
          <MillimetreField
            label={t('measurements.fields.totalPd')}
            value={total}
            onChange={(value) => {
              setTotalTouched(true);
              set('totalPdMm', value);
            }}
          />
        </div>

        <FormField label={t('measurements.fields.pdType')} hint={t('measurements.pdTypeHint')}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={pdType}
              options={[
                { value: 'DISTANCE', label: t('measurements.pdTypes.DISTANCE') },
                { value: 'NEAR', label: t('measurements.pdTypes.NEAR') },
              ]}
              onChange={(event) => setPdType(event.target.value as PdType)}
            />
          )}
        </FormField>

        <div className="grid gap-3 sm:grid-cols-2">
          <MillimetreField
            label={t('measurements.fields.faceWidth')}
            value={values.faceWidthMm}
            onChange={(value) => set('faceWidthMm', value)}
          />
          <MillimetreField
            label={t('measurements.fields.bridgeWidth')}
            value={values.bridgeWidthMm}
            onChange={(value) => set('bridgeWidthMm', value)}
          />
          <MillimetreField
            label={t('measurements.fields.eyeWidth')}
            value={values.eyeWidthMm}
            onChange={(value) => set('eyeWidthMm', value)}
          />
          <MillimetreField
            label={t('measurements.fields.faceLength')}
            value={values.faceLengthMm}
            onChange={(value) => set('faceLengthMm', value)}
          />
        </div>

        <FormField label={t('measurements.fields.notes')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={values.notes}
              maxLength={1000}
              onChange={(event) => set('notes', event.target.value)}
            />
          )}
        </FormField>

        <p className="text-muted text-xs">{t('measurements.optionalHint')}</p>
      </div>
    </Modal>
  );
}

/** A millimetre entry. Text, not a number input — the value is a decimal string end to end. */
function MillimetreField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <FormField label={label}>
      {({ id, describedBy }) => (
        <Input
          id={id}
          aria-describedby={describedBy}
          inputMode="decimal"
          dir="ltr"
          placeholder="—"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </FormField>
  );
}
